/* Scroll-snap carousel + Swiper hero gallery for the Nesti landing page. */

class NestiCarousel extends HTMLElement {
  connectedCallback() {
    // Guard: docking moves this node and re-fires connectedCallback.
    if (this.nestiCarouselBound) return;

    this.track = this.querySelector('.nesti-carousel__track');
    this.slides = Array.from(this.querySelectorAll('.nesti-carousel__slide'));
    this.dots = Array.from(this.querySelectorAll('.nesti-carousel__dot'));
    this.prev = this.querySelector('[data-carousel-prev]');
    this.next = this.querySelector('[data-carousel-next]');

    if (!this.track || this.slides.length < 2) return;

    this.onDotClick = (event) => {
      const index = this.dots.indexOf(event.currentTarget);
      if (index >= 0) this.scrollToSlide(index);
    };
    this.onPrevClick = () => this.scrollToSlide(this.activeIndex - 1);
    this.onNextClick = () => this.scrollToSlide(this.activeIndex + 1);
    this.onTrackScroll = () => {
      cancelAnimationFrame(this.frame);
      this.frame = requestAnimationFrame(() => {
        const center = this.track.scrollLeft + this.track.clientWidth / 2;
        const index = this.slides.findIndex((slide) => slide.offsetLeft + slide.clientWidth > center);
        this.setActive(index === -1 ? this.slides.length - 1 : index);
      });
    };

    this.dots.forEach((dot) => dot.addEventListener('click', this.onDotClick));
    this.prev?.addEventListener('click', this.onPrevClick);
    this.next?.addEventListener('click', this.onNextClick);
    this.track.addEventListener('scroll', this.onTrackScroll, { passive: true });

    this.nestiCarouselBound = true;
    this.activeIndex = 0;
    this.setActive(0);
  }

  scrollToSlide(index) {
    const target = this.slides[Math.min(Math.max(index, 0), this.slides.length - 1)];
    if (!target) return;
    this.track.scrollTo({ left: target.offsetLeft - this.track.offsetLeft, behavior: 'smooth' });
  }

  setActive(index) {
    this.activeIndex = index;
    this.dots.forEach((dot, dotIndex) => dot.setAttribute('aria-current', String(dotIndex === index)));
    if (this.prev) this.prev.disabled = index === 0;
    if (this.next) this.next.disabled = index === this.slides.length - 1;
  }
}

if (!customElements.get('nesti-carousel')) {
  customElements.define('nesti-carousel', NestiCarousel);
}

function normalizeGallerySlideWidths(swiper) {
  const width = Math.round(swiper.width);
  if (!width) return;
  swiper.slides.forEach((slide) => {
    slide.style.width = `${width}px`;
  });
  swiper.update();
}

function syncGalleryDotsAria(swiper, paginationEl) {
  paginationEl.querySelectorAll('.nesti-gallery__dot').forEach((dot, index) => {
    dot.setAttribute('aria-current', String(index === swiper.activeIndex));
  });
}

function patchGalleryScrollTo(wrapper, swiper) {
  wrapper.scrollTo = function scrollTo(options) {
    if (!options || typeof options.left !== 'number') return;

    const slideWidth = swiper.width || wrapper.clientWidth;
    if (!slideWidth) return;

    const index = Math.min(
      Math.max(Math.round(options.left / slideWidth), 0),
      swiper.slides.length - 1
    );
    const speed = options.behavior === 'smooth' ? swiper.params.speed : 0;
    swiper.slideTo(index, speed);
  };
}

function replaceNavButton(controls, name) {
  const button = controls.querySelector(`[name="${name}"]`);
  if (!button) return null;
  const clone = button.cloneNode(true);
  button.replaceWith(clone);
  return clone;
}

function dispatchGallerySlideChanged(viewer, swiper) {
  viewer.dispatchEvent(
    new CustomEvent('slideChanged', {
      bubbles: true,
      detail: {
        currentPage: swiper.activeIndex + 1,
        currentElement: swiper.slides[swiper.activeIndex],
      },
    })
  );
}

function disableDawnSliderComponent(viewer) {
  if (!viewer || viewer.nestiSliderNeutralized) return;
  viewer.nestiSliderNeutralized = true;
  viewer.update = () => {};
  viewer.initPages = () => {};
  viewer.resetPages = () => {};
}

function areNestiThumbnailsVisible(gallery) {
  const thumbs = gallery.querySelector('[id^="GalleryThumbnails"]');
  return Boolean(thumbs && thumbs.offsetParent !== null);
}

function scheduleNestiGalleryRefresh(gallery) {
  if (gallery.nestiSwiperRefreshPending) return;
  gallery.nestiSwiperRefreshPending = true;
  requestAnimationFrame(() => {
    gallery.nestiSwiperRefreshPending = false;
    const wrapper = gallery.querySelector('.product__media-list');
    gallery.nestiSwiperObserver?.disconnect();
    initNestiGallerySwiper(gallery);
    if (wrapper && gallery.nestiSwiperObserver) {
      gallery.nestiSwiperObserver.observe(wrapper, { childList: true });
    }
  });
}

function initNestiGallerySwiper(root = document) {
  if (typeof Swiper === 'undefined') return;

  const galleries = root.matches?.('.nesti-gallery')
    ? [root]
    : Array.from(root.querySelectorAll('.nesti-gallery'));

  galleries.forEach((gallery) => {
    const viewer = gallery.querySelector('slider-component');
    const wrapper = viewer?.querySelector('.product__media-list');
    const controls = gallery.querySelector('.slider-buttons');
    if (!viewer || !wrapper || !controls) return;

    const slides = wrapper.querySelectorAll('.product__media-item');
    if (slides.length < 2) return;

    gallery.nestiSwiperObserver?.disconnect();

    if (gallery.nestiSwiper) {
      gallery.nestiSwiper.destroy(true, true);
      gallery.nestiSwiper = null;
    }

    disableDawnSliderComponent(viewer);

    viewer.classList.add('swiper', 'nesti-gallery-swiper');
    wrapper.classList.add('swiper-wrapper');
    wrapper.classList.remove('grid', 'grid--peek', 'slider', 'slider--mobile');
    slides.forEach((slide) => {
      slide.classList.add('swiper-slide');
      slide.classList.remove('grid__item', 'slider__slide');
    });

    let paginationEl = controls.querySelector('.nesti-gallery__dots');
    if (!paginationEl) {
      paginationEl = document.createElement('div');
      paginationEl.className = 'nesti-gallery__dots';
      paginationEl.setAttribute('role', 'tablist');
      paginationEl.setAttribute('aria-label', 'Gallery slides');
      controls.appendChild(paginationEl);
    }

    /* Clone Dawn nav buttons so SliderComponent listeners are dropped. */
    const prevEl = replaceNavButton(controls, 'previous');
    const nextEl = replaceNavButton(controls, 'next');

    const swiper = new Swiper(viewer, {
      slidesPerView: 1,
      slidesPerGroup: 1,
      spaceBetween: 0,
      speed: 350,
      grabCursor: true,
      simulateTouch: true,
      allowTouchMove: true,
      resistanceRatio: 0.85,
      threshold: 5,
      touchRatio: 1,
      touchStartPreventDefault: false,
      passiveListeners: true,
      shortSwipes: true,
      longSwipes: true,
      longSwipesRatio: 0.25,
      followFinger: true,
      watchOverflow: true,
      roundLengths: true,
      preventClicks: true,
      preventClicksPropagation: true,
      observer: true,
      observeParents: true,
      resizeObserver: true,
      updateOnWindowResize: true,
      navigation: prevEl && nextEl ? { prevEl, nextEl } : undefined,
      pagination: {
        el: paginationEl,
        clickable: true,
        bulletElement: 'button',
        bulletClass: 'nesti-gallery__dot',
        bulletActiveClass: 'nesti-gallery__dot--active',
        renderBullet(index, className) {
          return `<button type="button" class="${className}" aria-label="Go to slide ${index + 1}"></button>`;
        },
      },
      on: {
        init(sw) {
          requestAnimationFrame(() => normalizeGallerySlideWidths(sw));
        },
        slideChange(sw) {
          syncGalleryDotsAria(sw, paginationEl);
          dispatchGallerySlideChanged(viewer, sw);
        },
      },
    });

    requestAnimationFrame(() => normalizeGallerySlideWidths(swiper));

    syncGalleryDotsAria(swiper, paginationEl);
    gallery.nestiSwiper = swiper;

    patchGalleryScrollTo(wrapper, swiper);
    bindNestiGalleryThumbnails(gallery, swiper);

    viewer.setSlidePosition = (position) => {
      const slideWidth = swiper.width || wrapper.clientWidth;
      if (!slideWidth) return;
      swiper.slideTo(Math.round(position / slideWidth), swiper.params.speed);
    };

    if (!gallery.nestiSwiperObserver) {
      gallery.nestiSwiperObserver = new MutationObserver(() => scheduleNestiGalleryRefresh(gallery));
    }
    gallery.nestiSwiperObserver.observe(wrapper, { childList: true });
  });
}

function initNestiHowtoVideos(root = document) {
  const videos = root.querySelectorAll('[data-nesti-howto-video] video, .nesti-science__media--video video');

  videos.forEach((video) => {
    // Match GemPages: always show a muted looping clip with no player chrome.
    // Do not pause for prefers-reduced-motion — that left a frozen/blank media card.
    video.controls = false;
    video.removeAttribute('controls');
    video.setAttribute('playsinline', '');
    video.setAttribute('autoplay', '');
    video.setAttribute('loop', '');
    video.muted = true;
    video.loop = true;
    video.autoplay = true;

    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  });
}

/* Thumbnail tray infinite loop: [prepend clones | originals | append clones].
   Start in the middle set so drag works both ways on first load. Dawn still
   finds real items via :not([data-nesti-clone]); clicks use event delegation. */
function getNestiThumbnailOriginals(list) {
  return Array.from(
    list.querySelectorAll('.thumbnail-list__item[data-target]:not([data-nesti-clone])')
  );
}

function cloneNestiThumbnailItem(item) {
  const clone = item.cloneNode(true);
  clone.dataset.nestiClone = 'true';
  /* Keep media id for our click handler, but drop data-target so Dawn's
     querySelector(`[data-target]`) still resolves to the real middle-set item
     (prepend clones would otherwise win and scrollLeft jumps to ~0). */
  if (item.dataset.target) clone.dataset.nestiTarget = item.dataset.target;
  clone.removeAttribute('data-target');
  clone.querySelectorAll('[id]').forEach((el) => el.removeAttribute('id'));
  clone.querySelector('button')?.removeAttribute('aria-current');
  return clone;
}

function getNestiThumbnailMediaId(item) {
  return item?.dataset?.target || item?.dataset?.nestiTarget || '';
}

function measureNestiThumbnailSetWidth(list) {
  const originals = getNestiThumbnailOriginals(list);
  if (originals.length < 2) return 0;
  const first = originals[0];
  const last = originals[originals.length - 1];
  const gap = parseFloat(getComputedStyle(list).columnGap || getComputedStyle(list).gap) || 0;
  return last.offsetLeft + last.offsetWidth + gap - first.offsetLeft;
}

function refreshNestiThumbnailSetWidth(list) {
  list.nestiThumbSetWidth = measureNestiThumbnailSetWidth(list);
  return list.nestiThumbSetWidth;
}

function ensureNestiThumbnailLoop(gallery) {
  const list = gallery.querySelector('.thumbnail-list');
  if (!list) return null;

  const originals = getNestiThumbnailOriginals(list);
  if (originals.length < 2) return list;

  if (list.dataset.nestiThumbLoop !== 'bidirectional') {
    list.querySelectorAll('.thumbnail-list__item[data-nesti-clone]').forEach((el) => el.remove());

    const prependFragment = document.createDocumentFragment();
    const appendFragment = document.createDocumentFragment();
    originals.forEach((item) => {
      prependFragment.appendChild(cloneNestiThumbnailItem(item));
      appendFragment.appendChild(cloneNestiThumbnailItem(item));
    });
    list.insertBefore(prependFragment, originals[0]);
    list.appendChild(appendFragment);
    list.dataset.nestiThumbLoop = 'bidirectional';
  }

  refreshNestiThumbnailSetWidth(list);
  /* Land in the middle (original) set so left drag wraps immediately. */
  const settle = () => {
    refreshNestiThumbnailSetWidth(list);
    normalizeNestiThumbnailScroll(list);
  };
  settle();
  requestAnimationFrame(settle);
  return list;
}

/* Keep scrollLeft inside the middle set by jumping ± one set width. */
function normalizeNestiThumbnailScroll(list, dragState) {
  if (list.nestiThumbProgrammaticScroll) return;
  const setWidth = list.nestiThumbSetWidth || refreshNestiThumbnailSetWidth(list);
  if (!setWidth) return;

  let jumps = 0;
  const maxJumps = 20;

  while (list.scrollLeft >= setWidth * 2 && jumps < maxJumps) {
    list.scrollLeft -= setWidth;
    if (dragState) dragState.startScroll -= setWidth;
    jumps += 1;
  }
  while (list.scrollLeft < setWidth && jumps < maxJumps) {
    list.scrollLeft += setWidth;
    if (dragState) dragState.startScroll += setWidth;
    jumps += 1;
  }
}

function syncNestiThumbnailAria(gallery, mediaId) {
  if (!mediaId) return;
  gallery
    .querySelectorAll('.thumbnail-list__item[data-target], .thumbnail-list__item[data-nesti-target]')
    .forEach((item) => {
      const button = item.querySelector('button');
      if (!button) return;
      if (getNestiThumbnailMediaId(item) === mediaId) {
        button.setAttribute('aria-current', 'true');
      } else {
        button.removeAttribute('aria-current');
      }
    });
}

function findNestiThumbnailForScroll(list, mediaId) {
  if (mediaId) {
    return (
      list.querySelector(`.thumbnail-list__item[data-target="${mediaId}"]:not([data-nesti-clone])`) ||
      list.querySelector(`.thumbnail-list__item[data-target="${mediaId}"]`)
    );
  }

  return list
    .querySelector('.thumbnail-list__item:not([data-nesti-clone]) button[aria-current="true"]')
    ?.closest('.thumbnail-list__item');
}

function scrollNestiThumbnailToStart(gallery, mediaId) {
  if (!areNestiThumbnailsVisible(gallery)) return;

  const list = ensureNestiThumbnailLoop(gallery);
  if (!list) return;

  normalizeNestiThumbnailScroll(list);
  const item = findNestiThumbnailForScroll(list, mediaId);
  if (!item) return;

  const clearProgrammatic = () => {
    list.nestiThumbProgrammaticScroll = false;
    list.removeEventListener('scrollend', clearProgrammatic);
  };

  list.nestiThumbProgrammaticScroll = true;
  list.addEventListener('scrollend', clearProgrammatic, { once: true });
  window.setTimeout(clearProgrammatic, 450);

  list.scrollTo({ left: item.offsetLeft, behavior: 'smooth' });
  syncNestiThumbnailAria(gallery, mediaId || getNestiThumbnailMediaId(item));
}

function initNestiThumbnailDrag(gallery) {
  const list = gallery.querySelector('.thumbnail-list');
  if (!list || list.nestiDragBound) return;
  list.nestiDragBound = true;

  const dragThreshold = 6;
  const dragState = { startScroll: 0 };
  let pointerId = null;
  let startX = 0;
  let dragged = false;
  let capturing = false;

  /* Defer setPointerCapture until a real drag. Capturing on pointerdown
     retargets the following click to the list, so thumbnail buttons never fire. */
  list.addEventListener('pointerdown', (event) => {
    if (event.button !== 0) return;
    pointerId = event.pointerId;
    startX = event.clientX;
    dragState.startScroll = list.scrollLeft;
    dragged = false;
    capturing = false;
  });

  list.addEventListener('pointermove', (event) => {
    if (pointerId === null || event.pointerId !== pointerId) return;
    const delta = event.clientX - startX;
    if (!dragged && Math.abs(delta) <= dragThreshold) return;

    if (!dragged) {
      dragged = true;
      list.classList.add('is-dragging');
      try {
        list.setPointerCapture(pointerId);
        capturing = true;
      } catch (_) {
        capturing = false;
      }
    }
    list.scrollLeft = dragState.startScroll - delta;
    normalizeNestiThumbnailScroll(list, dragState);
  });

  const endDrag = (event) => {
    if (pointerId === null || event.pointerId !== pointerId) return;
    list.classList.remove('is-dragging');
    if (capturing) list.releasePointerCapture?.(pointerId);
    const wasDragged = dragged;
    pointerId = null;
    dragged = false;
    capturing = false;
    normalizeNestiThumbnailScroll(list);
    if (wasDragged) {
      list.nestiSuppressClickUntil = Date.now() + 250;
    }
  };

  list.addEventListener('pointerup', endDrag);
  list.addEventListener('pointercancel', endDrag);

  list.addEventListener(
    'scroll',
    () => {
      if (pointerId !== null) return;
      if (list.nestiThumbProgrammaticScroll) return;
      normalizeNestiThumbnailScroll(list);
    },
    { passive: true }
  );

  list.addEventListener(
    'click',
    (event) => {
      if (list.nestiSuppressClickUntil && Date.now() < list.nestiSuppressClickUntil) {
        event.preventDefault();
        event.stopPropagation();
      }
    },
    true
  );

  if (!list.nestiThumbResizeBound) {
    list.nestiThumbResizeBound = true;
    window.addEventListener('resize', () => refreshNestiThumbnailSetWidth(list));
  }
}

function bindNestiGalleryThumbnails(gallery, swiper) {
  const list = ensureNestiThumbnailLoop(gallery);
  if (!list) return;

  const thumbs = list.querySelectorAll('.thumbnail-list__item[data-target]');
  if (!thumbs.length) return;

  initNestiThumbnailDrag(gallery);

  /* Delegate so clone thumbs (no Dawn listeners) still select the main slide. */
  if (!list.nestiThumbClickBound) {
    list.nestiThumbClickBound = true;
    list.addEventListener('click', (event) => {
      const button = event.target.closest('.thumbnail-list__item button');
      if (!button || !list.contains(button)) return;
      if (list.nestiSuppressClickUntil && Date.now() < list.nestiSuppressClickUntil) return;

      const item = button.closest('.thumbnail-list__item');
      const mediaId = getNestiThumbnailMediaId(item);
      if (!mediaId) return;

      const activeSwiper = gallery.nestiSwiper;
      if (!activeSwiper) return;

      const slides = gallery.querySelectorAll('.product__media-item');
      const index = Array.from(slides).findIndex((slide) => slide.dataset.mediaId === mediaId);
      if (index >= 0) activeSwiper.slideTo(index);
      syncNestiThumbnailAria(gallery, mediaId);
      scrollNestiThumbnailToStart(gallery, mediaId);
    });
  }

  if (!swiper.nestiThumbSlideBound) {
    swiper.nestiThumbSlideBound = true;
    swiper.on('slideChange', () => {
      const active = swiper.slides[swiper.activeIndex];
      const mediaId = active?.dataset?.mediaId;
      if (!mediaId) return;
      if (!areNestiThumbnailsVisible(gallery)) return;
      syncNestiThumbnailAria(gallery, mediaId);
      scrollNestiThumbnailToStart(gallery, mediaId);
    });
  }
}

document.addEventListener('shopify:section:load', (event) => {
  initNestiGallerySwiper(event.target);
  initNestiHowtoVideos(event.target);
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    initNestiGallerySwiper();
    initNestiHowtoVideos();
  });
} else {
  initNestiGallerySwiper();
  initNestiHowtoVideos();
}

window.addEventListener('load', () => {
  document.querySelectorAll('.nesti-gallery').forEach((gallery) => {
    if (gallery.nestiSwiper) normalizeGallerySlideWidths(gallery.nestiSwiper);
  });
});

window.addEventListener('resize', () => {
  document.querySelectorAll('.nesti-gallery').forEach((gallery) => {
    if (gallery.nestiSwiper) normalizeGallerySlideWidths(gallery.nestiSwiper);
  });
});
